import { useQuery, useSubscription } from '@apollo/client/react'

import {
  advisorsAttentionTotal,
  advisorsNavAttention,
  toAdvisorsAttention,
} from '@src/components/sco/Advisors/utils/advisorsAttention'
import { ADVISORS_ATTENTION, ADVISORS_ATTENTION_DIRECT_CHAT_VIEWED } from '@src/gql/queries/AdvisorsAttention'
import type { ScoAdvisorsAttentionCounts } from '@src/graphql-types'
import { useCurrentUser } from '@src/hooks/useCurrentUser'
import { useEffect } from 'react'

type AdvisorsAttentionData = {
  currentUser?: { id?: string; advisorsAttention?: ScoAdvisorsAttentionCounts | null } | null
}

/** Focus refetches from every mounted caller collapse into one request per window. */
const FOCUS_REFETCH_MIN_INTERVAL_MS = 2000
let lastFocusRefetchAt = 0

const claimFocusRefetch = (): boolean => {
  const now = Date.now()
  if (now - lastFocusRefetchAt < FOCUS_REFETCH_MIN_INTERVAL_MS) {
    return false
  }
  lastFocusRefetchAt = now
  return true
}

/**
 * The one shared read behind every Advisors / Chat badge (W3).
 *
 * Every caller (site nav, Chat icon, `AdvisorsNav`, inner rails) runs the same cache-first
 * `currentUser.advisorsAttention` query, so Apollo serves them all from one request.
 *
 * Freshness, no timer polling:
 * - window focus (throttled across callers),
 * - `scoUserDirectChatViewed` (the viewer read a direct-chat thread somewhere),
 * - `refetch`, which clearing mutations call — or they add `ADVISORS_ATTENTION_REFETCH` to their
 *   `refetchQueries`.
 *
 * Guests never send the query.
 */
export const useAdvisorsAttention = () => {
  const { currentUser } = useCurrentUser()
  const enabled = !!currentUser?.isRegistered && !currentUser?.isGuest

  const query = useQuery<AdvisorsAttentionData>(ADVISORS_ATTENTION, {
    ssr: false,
    skip: !enabled,
  })
  const { refetch } = query

  useSubscription(ADVISORS_ATTENTION_DIRECT_CHAT_VIEWED, {
    skip: !enabled,
    onData: () => {
      void refetch()
    },
  })

  useEffect(() => {
    if (!enabled) {
      return
    }
    const onFocus = () => {
      if (claimFocusRefetch()) {
        void refetch()
      }
    }
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [enabled, refetch])

  const attention = toAdvisorsAttention(query.data?.currentUser?.advisorsAttention)

  return {
    attention,
    nav: advisorsNavAttention(attention),
    total: advisorsAttentionTotal(attention),
    loading: query.loading && !query.data,
    refetch,
  }
}
